// Collection "Load more": fetch the next page via the Section Rendering API and
// append its product cards in place. The Liquid section decides the next URL
// (including the availability phase flip), so this script is a dumb appender.
//
// The button doubles as an infinite-scroll sentinel: an IntersectionObserver
// loads it before it enters the viewport. Because appended cards do not exist
// after a full-page trip to a PDP, the component records the loaded pages in
// this history entry and rebuilds them before restoring the viewport.
const COLLECTION_HISTORY_KEY = 'sylvieCollection';
const COLLECTION_HISTORY_VERSION = 1;

class CollectionLoadMore extends HTMLElement {
	connectedCallback() {
		this.sectionId = this.dataset.sectionId;
		// A result set can be empty, in which case the section renders an empty
		// state and no grid. Set the component up regardless: a filter change
		// can bring the grid back without this element ever reconnecting.
		this.grid = this.querySelector('.collection-grid');
		this.pageKey = this.getPageKey(window.location.href);
		this.restoring = false;

		const savedState = this.getSavedState();
		this.loadedPages = savedState?.loadedPages?.slice() || [];

		this.onClickHandler = this.onClick.bind(this);
		this.onPageHideHandler = this.savePosition.bind(this);
		this.onPageShowHandler = this.onPageShow.bind(this);
		this.addEventListener('click', this.onClickHandler);
		window.addEventListener('pagehide', this.onPageHideHandler);
		window.addEventListener('pageshow', this.onPageShowHandler);

		// Native restoration runs while only page one exists and can clamp the
		// old position. Restore only after the complete grid is rebuilt.
		if ('scrollRestoration' in history) history.scrollRestoration = 'manual';

		this.observer = new IntersectionObserver(
			(entries) => {
				if (this.restoring) return;
				entries.forEach((entry) => {
					if (!entry.isIntersecting) return;
					const btn = entry.target.querySelector('[data-load-more]');
					if (btn) this.loadNext(btn);
				});
			},
			// Start fetching ~2 viewports early so the next page is usually in
			// place before the user reaches the end of the grid.
			{ rootMargin: '200% 0px' }
		);

		if (savedState && this.grid) {
			this.restore(savedState);
		} else {
			this.observeSentinel();
		}
	}

	// The filter and sort sheets re-render the results through the Section
	// Rendering API and swap the new markup in around this element, which keeps
	// its identity — so connectedCallback does not run again and the observer is
	// left watching a detached sentinel. They call this once the swap is done.
	resync() {
		this.grid = this.querySelector('.collection-grid');
		// A new result set: whatever had been paged in no longer applies, and
		// state must be saved under the filtered URL from here on.
		this.pageKey = this.getPageKey(window.location.href);
		this.loadedPages = [];
		this.restoring = false;
		this.observeSentinel();
	}

	disconnectedCallback() {
		this.observer?.disconnect();
		this.removeEventListener('click', this.onClickHandler);
		window.removeEventListener('pagehide', this.onPageHideHandler);
		window.removeEventListener('pageshow', this.onPageShowHandler);
	}

	getPageKey(value) {
		const url = new URL(value, window.location.origin);
		url.hash = '';
		url.searchParams.delete('section_id');
		return url.pathname + url.search;
	}

	getSavedState() {
		const state = history.state?.[COLLECTION_HISTORY_KEY];
		if (
			!state ||
			state.version !== COLLECTION_HISTORY_VERSION ||
			state.pageKey !== this.pageKey ||
			state.sectionId !== this.sectionId
		) {
			return null;
		}
		return state;
	}

	saveState(changes = {}) {
		const current = this.getSavedState() || {
			version: COLLECTION_HISTORY_VERSION,
			pageKey: this.pageKey,
			sectionId: this.sectionId,
			loadedPages: this.loadedPages.slice(),
		};
		const rootState = history.state && typeof history.state === 'object' ? history.state : {};
		history.replaceState(
			{
				...rootState,
				[COLLECTION_HISTORY_KEY]: { ...current, ...changes },
			},
			'',
			window.location.href
		);
	}

	savePosition() {
		this.saveState({ scrollY: window.scrollY });
	}

	observeSentinel() {
		this.observer.disconnect();
		const wrapper = this.querySelector('.collection-loadmore');
		if (wrapper) this.observer.observe(wrapper);
	}

	onClick(event) {
		const btn = event.target.closest('[data-load-more]');
		if (btn) {
			event.preventDefault();
			this.loadNext(btn);
			return;
		}

		const productLink = event.target.closest('.collection-grid__item a[href]');
		if (!productLink) return;

		const item = productLink.closest('[data-product-id]');
		const destination = new URL(productLink.href, window.location.origin);
		if (
			!item ||
			destination.origin !== window.location.origin ||
			!destination.pathname.includes('/products/')
		) {
			return;
		}

		this.saveState({
			scrollY: window.scrollY,
			productId: item.dataset.productId,
			productOffset: item.getBoundingClientRect().top,
			loadedPages: this.loadedPages.slice(),
		});
	}

	onPageShow(event) {
		// A bfcache return keeps the appended DOM, so reconstruction is
		// unnecessary. Manual restoration still needs a position correction.
		if (event.persisted) this.restorePosition(this.getSavedState());
	}

	async restore(savedState) {
		this.restoring = true;
		this.observer.disconnect();

		for (let index = 0; index < savedState.loadedPages.length; index += 1) {
			const btn = this.querySelector('[data-load-more]');
			if (!btn) break;
			const loaded = await this.loadNext(btn, {
				record: false,
				fallback: false,
				observe: false,
			});
			if (!loaded) break;
		}

		await this.waitForLayout();
		this.restorePosition(savedState);
		this.restoring = false;
		this.observeSentinel();
	}

	// Page the whole collection in. In-collection search needs every card in
	// the DOM before it can say how many match, and these collections are
	// small enough to ask for — three to five pages. The cap is a stop for a
	// next_url that somehow never resolves, not an expected limit.
	async loadEveryPage(onPage, cap = 40) {
		this.observer?.disconnect();
		for (let index = 0; index < cap; index += 1) {
			const btn = this.querySelector('[data-load-more]');
			if (!btn) return true;
			const loaded = await this.loadNext(btn, { record: false, fallback: false, observe: false });
			if (!loaded) return false;
			// The caller sees each page as it lands, rather than the lot at the
			// end: in-collection search has matches to show long before the
			// last page is in.
			onPage?.();
		}
		return false;
	}

	waitForLayout() {
		return new Promise((resolve) => {
			requestAnimationFrame(() => requestAnimationFrame(resolve));
		});
	}

	restorePosition(savedState) {
		if (!savedState || !this.grid) return;

		const item = Array.from(this.grid.querySelectorAll('[data-product-id]')).find(
			(candidate) => candidate.dataset.productId === savedState.productId
		);
		if (item && Number.isFinite(savedState.productOffset)) {
			window.scrollBy(0, item.getBoundingClientRect().top - savedState.productOffset);
		} else if (Number.isFinite(savedState.scrollY)) {
			window.scrollTo(0, savedState.scrollY);
		}
	}

	async loadNext(btn, options = {}) {
		const { record = true, fallback = true, observe = true } = options;
		if (btn.getAttribute('aria-busy') === 'true') return false;

		const wrapper = btn.closest('.collection-loadmore');
		const pageUrl = this.getPageKey(btn.href);
		const url = new URL(btn.href, window.location.origin);
		url.searchParams.set('section_id', this.sectionId);

		btn.setAttribute('aria-busy', 'true');
		try {
			const res = await fetch(url.toString());
			if (!res.ok) throw new Error('Load more failed: ' + res.status);
			const doc = new DOMParser().parseFromString(await res.text(), 'text/html');

			const items = doc.querySelectorAll('.collection-grid > li');
			items.forEach((li) => this.grid.appendChild(li));

			if (record) {
				this.loadedPages.push(pageUrl);
				this.saveState({ loadedPages: this.loadedPages.slice(), scrollY: window.scrollY });
			}

			const nextWrapper = doc.querySelector('.collection-loadmore');
			if (nextWrapper && items.length > 0) {
				wrapper.replaceWith(nextWrapper);
				if (observe) this.observeSentinel();
			} else {
				// No further page, or an empty page (e.g. the sold-out phase has
				// nothing): pagination is exhausted — drop the button.
				wrapper.remove();
				this.observer.disconnect();
			}
			return true;
		} catch (err) {
			btn.removeAttribute('aria-busy');
			if (fallback) {
				// Graceful fallback for ordinary browsing: use normal pagination.
				window.location.href = btn.href;
			} else {
				console.warn('Could not restore the complete collection grid.', err);
			}
			return false;
		}
	}
}
customElements.define('collection-load-more', CollectionLoadMore);
